<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-28
-->

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from '#imports';
import type { Course, Teacher } from '~/types/courses';

defineProps<{
  course: Course;
}>();

const { t } = useI18n();
const imageLoadErrors = ref<Record<string, boolean>>({});

const getUniqueSubjects = (course: Course) => {
  if (Array.isArray(course.courses)) {
    const subjects = course.courses.map((c) => c.subject?.name).filter(Boolean);
    return [...new Set(subjects)];
  }
  return course.subject?.name ? [course.subject.name] : [];
};

const getUniqueTeachers = (course: Course) => {
  if (Array.isArray(course.courses)) {
    const teachersMap = new Map<string, Teacher>();
    for (const sub of course.courses) {
      if (sub.teacher?._id) {
        teachersMap.set(sub.teacher._id.toString(), sub.teacher);
      }
    }
    return Array.from(teachersMap.values());
  }
  return course.teacher?._id ? [course.teacher] : [];
};
</script>

<template>
  <div class="mt-2 mb-3 flex flex-col gap-2 rounded-xl bg-slate-50 p-2.5 text-xs text-slate-600">
    <!-- Overlapping Avatars & Lecturers Count / Subjects & Courses count info -->
    <div class="flex items-center justify-between gap-2">
      <!-- Left side: Avatars & Count -->
      <div class="flex items-center gap-2">
        <div class="flex shrink-0 -space-x-2">
          <div
            v-for="teacher in (course.teachers || getUniqueTeachers(course)).slice(0, 4)"
            :key="teacher._id"
            class="relative h-7 w-7 overflow-hidden rounded-full border border-white bg-slate-200 shadow-xs"
            :title="teacher.name"
          >
            <AppImage
              v-if="teacher.photo && !imageLoadErrors[teacher._id]"
              :src="teacher.photo"
              :alt="teacher.name"
              img-class="h-full w-full rounded-full object-cover"
              loader-size="h-3.5 w-3.5"
              loader-overlay-class="bg-slate-50"
              loader-text=""
              @error="imageLoadErrors[teacher._id] = true"
            />
            <div
              v-else
              class="flex h-full w-full items-center justify-center rounded-full bg-brand-blue/5 text-[9px] font-bold text-brand-blue uppercase"
            >
              {{ teacher.name ? teacher.name.substring(0, 2) : '👨‍🏫' }}
            </div>
          </div>
        </div>
        <span class="text-[10px] font-bold whitespace-nowrap text-slate-800">
          {{ t('courses.teachersCount', { count: course.total_teachers ?? getUniqueTeachers(course).length }) }}
        </span>
      </div>

      <!-- Right side: Subjects & Courses count info -->
      <span class="text-right text-[10px] font-bold text-slate-500">
        {{
          t('courses.inclusionsCount', {
            subjects: getUniqueSubjects(course).length,
            courses: course.total_courses ?? course.courses_count ?? course.courses?.length ?? 0,
          })
        }}
      </span>
    </div>

    <!-- Features Icon Row -->
    <div class="mt-1 grid grid-cols-5 gap-1 rounded-lg border border-slate-100/50 bg-white p-1.5 shadow-xs">
      <!-- Videos -->
      <div class="flex min-w-0 flex-col items-center text-center">
        <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
          <Icon name="lucide:video" class="h-4 w-4" />
        </div>
        <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
          {{ course.total_video || 0 }}{{ (course.total_courses ?? course.courses_count ?? 0) > 10 ? '+' : '' }}
          {{ t('courses.vids') }}
        </span>
      </div>

      <!-- PDF Docs -->
      <div class="flex min-w-0 flex-col items-center text-center">
        <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
          <Icon name="lucide:file-text" class="h-4 w-4" />
        </div>
        <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
          {{ course.total_pdf || 0 }}{{ (course.total_courses ?? course.courses_count ?? 0) > 10 ? '+' : '' }} PDF
        </span>
      </div>

      <!-- Support -->
      <div class="flex min-w-0 flex-col items-center text-center">
        <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
          <Icon name="lucide:headphones" class="h-4 w-4" />
        </div>
        <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800"> E-Teach </span>
      </div>

      <!-- Quizzes -->
      <div class="flex min-w-0 flex-col items-center text-center">
        <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
          <Icon name="lucide:help-circle" class="h-4 w-4" />
        </div>
        <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
          {{ t('courses.quizzes') }}
        </span>
      </div>

      <!-- Exam -->
      <div class="flex min-w-0 flex-col items-center text-center">
        <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-blue/5 text-brand-blue">
          <Icon name="lucide:graduation-cap" class="h-4 w-4" />
        </div>
        <span class="mt-1 w-full truncate text-[8px] leading-tight font-black text-slate-800">
          {{ t('courses.exams') }}
        </span>
      </div>
    </div>
  </div>
</template>
